import type { PayoutResponse } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import type { PayinRuntimeEntity } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { taxLinesFromBreakdown } from "@/shared/utils/paymentTaxes";
import { payoutBrebKey } from "@/shared/utils/transferReceipt/transferReceipt";

import {
  PAYIN_NON_TERMINAL,
  PAYIN_STATUS_BUCKET,
  PAYOUT_NON_TERMINAL,
  PAYOUT_STATUS_BUCKET,
} from "../constants/paymentsActivity.constants";
import type { ActivityRow } from "../types/paymentsActivity.types";

/**
 * PayinEntity → normalized row. Amount precedence: `net_amount` (what gets credited) → received gross →
 * expected gross (QR minted, nothing received yet) → "0". Money stays a decimal string until render.
 */
export function adaptPayinRow(payin: PayinRuntimeEntity): ActivityRow {
  return {
    direction: "payins",
    id: payin.id,
    status: payin.status,
    bucket: PAYIN_STATUS_BUCKET[payin.status] ?? "pending",
    isNonTerminal: PAYIN_NON_TERMINAL.has(payin.status),
    country: payin.country,
    currency: payin.currency,
    rail: payin.rail,
    amount: payin.net_amount ?? payin.gross_amount_received ?? payin.gross_amount_expected ?? "0",
    grossAmount: payin.gross_amount_received ?? payin.gross_amount_expected,
    feeTotal: payin.fee_total,
    taxes: taxLinesFromBreakdown(payin.fee_breakdown?.taxes),
    netAmount: payin.net_amount,
    providerReference: payin.provider_reference,
    concept: null,
    evidenceUrl: payin.evidence_url ?? null,
    failureReason: payin.failure_reason,
    createdAt: payin.created_at,
    updatedAt: payin.updated_at,
    expiresAt: payin.expires_at ?? null,
    qrCodePayload: payin.qr_code_payload ?? null,
    breb: payin.breb ?? null,
    beneficiaryName: null,
    brebKey: null,
    brebNetworkReference: null,
    settledAt: payin.confirmed_at,
    failedAt: payin.failed_at,
    raw: payin,
  };
}

/** PayoutEntity → normalized row. `net_amount` is what the beneficiary receives (never null on payouts). */
export function adaptPayoutRow(payout: PayoutResponse): ActivityRow {
  return {
    direction: "payouts",
    id: payout.id,
    status: payout.status,
    bucket: PAYOUT_STATUS_BUCKET[payout.status] ?? "pending",
    isNonTerminal: PAYOUT_NON_TERMINAL.has(payout.status),
    country: payout.country,
    currency: payout.currency,
    rail: payout.rail,
    amount: payout.net_amount,
    grossAmount: payout.gross_amount,
    feeTotal: payout.fee_total,
    taxes: taxLinesFromBreakdown(payout.fee_breakdown?.taxes),
    netAmount: payout.net_amount,
    providerReference: payout.provider_reference,
    concept: payout.concept,
    evidenceUrl: null,
    failureReason: payout.failure_reason,
    createdAt: payout.created_at,
    updatedAt: payout.updated_at,
    expiresAt: null,
    qrCodePayload: null,
    breb: null,
    beneficiaryName: payout.beneficiary_name ?? null,
    brebKey: payoutBrebKey(payout.receipt),
    brebNetworkReference: payout.receipt?.network_reference?.trim() || null,
    settledAt: payout.paid_at,
    failedAt: payout.failed_at,
    raw: payout,
  };
}
